import React from 'react'
import {FormattedDate} from 'react-intl'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {
  Card,
  PaymentMethodType
} from '@vio/core/src/api-types/bovio/response/payment_methods_settings'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {calculateChargeDate} from '@vio/core/src/offer/business/chargeLater'
import {OfferPaymentMode} from '@vio/core/src/offer/business/offerPaymentMode'
import {CancellationPenalty} from '@vio/core/src/offer/types/offer'

import {PaymentMessage} from '../../../accommodation/OfferPerks/PaymentLabelType'
import {CreditCardLogo} from '../../CreditCardLogo/CreditCardLogo'
import {PaymentMethodLogo} from '../../PaymentMethodLogo/PaymentMethodLogo'

const AcceptedPaymentMethods = ({
  supportedCards,
  alternativePaymentMethodTypes
}: {
  supportedCards: Card[]
  alternativePaymentMethodTypes: PaymentMethodType[]
}) => {
  const alternativePaymentMethods = alternativePaymentMethodTypes.filter(
    type => type !== PaymentMethodType.Card
  )

  return (
    <Box display="flex" gap="s300" flexDirection="column">
      <Text variant="bodyM">
        <FormattedMessageWrapper
          id="paymentMethods.accepted"
          defaultMessage="We accept the following payment methods:"
        />
      </Text>
      <Box display="flex" gap="s300" flexWrap="wrap" alignItems="center">
        {supportedCards.map(card => (
          <CreditCardLogo
            key={card.code}
            brand={card.brand}
            name={card.brand}
          />
        ))}
        {alternativePaymentMethods.map(type => (
          <PaymentMethodLogo
            type={type}
            key={type}
            customStyle={{height: 16, width: 'auto'}}
          />
        ))}
      </Box>
    </Box>
  )
}

export const PaymentMethods = ({
  paymentMethodType,
  cancellationPenalties,
  supportedCards,
  availablePaymentMethods
}: {
  paymentMethodType: OfferPaymentMode
  cancellationPenalties: CancellationPenalty[]
  supportedCards: Card[]
  availablePaymentMethods: PaymentMethodType[]
}) => {
  const chargeDate = calculateChargeDate(cancellationPenalties)

  const hasNoPaymentMethods =
    supportedCards.length === 0 && availablePaymentMethods.length === 0
  if (
    hasNoPaymentMethods &&
    paymentMethodType !== OfferPaymentMode.PayAtProperty
  )
    return null

  const chargeLaterDescription = (
    <>
      <Text tag="span" variant="bodyM" colorPath="content.success.secondary">
        <FormattedMessageWrapper
          id="chargeLater.zeroRiskStay"
          defaultMessage="Book your stay with zero risk."
        />
      </Text>
      &nbsp;
      <FormattedMessageWrapper
        id="chargeLater.zeroRiskStayDescription"
        defaultMessage="We’ll secure your stay today, then charge you on {chargeDate}. You're free to cancel at no cost until then."
        values={{
          chargeDate: chargeDate ? (
            <FormattedDate
              value={chargeDate}
              month="long"
              day="numeric"
              year="numeric"
            />
          ) : (
            ''
          )
        }}
      />
    </>
  )

  return (
    <Box display="flex" flexDirection="column" gap="s200">
      <Text variant="titleM">
        <PaymentMessage paymentMode={paymentMethodType} />
      </Text>
      <Text variant="bodyM">
        {paymentMethodType === OfferPaymentMode.PayAtProperty ? (
          <FormattedMessageWrapper
            id="paymentMethods.payAtPropertyDescription"
            defaultMessage="This property accepts credit cards; cash is not accepted."
          />
        ) : paymentMethodType === OfferPaymentMode.ChargeLater ? (
          chargeLaterDescription
        ) : null}
        {paymentMethodType !== OfferPaymentMode.PayAtProperty && (
          <AcceptedPaymentMethods
            supportedCards={supportedCards}
            alternativePaymentMethodTypes={availablePaymentMethods}
          />
        )}
      </Text>
    </Box>
  )
}
